/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import CodeIcon from "@material-ui/icons/Code";
import HomeIcon from "@material-ui/icons/Home";
import TableChartIcon from "@material-ui/icons/TableChart";
import {
    NavBarLinkIconV1,
    NavBarLinkTextV1,
    NavBarLinkV1,
    NavBarPrimaryContainerV1,
    NavBarV1,
    useAuthProviderV1,
} from "@startree-ui/platform-ui";
import { isEmpty } from "lodash";
import React, { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Status } from "../../../rest/actions.interfaces";
import { useFetchDataSources } from "../../../rest/datasources/datasources.actions";
import {
    getDefaultPinotDatasource,
    getQueryConsoleUrl,
} from "../../../utils/pinot/pinot.utils";
import { AppRouteV2 } from "../../../utils/routes/routes.util";
import { useEnvironmentInfoContext } from "../../environment-info-provider/environment-info-provider.component";

export const AppNavBar: FunctionComponent = () => {
    const { authenticated, authDisabled, accessToken } = useAuthProviderV1();
    const [queryConsoleUrl, setQueryConsoleUrl] = useState<string | null>(null);
    const { t } = useTranslation();
    const {
        envFlags: { isStarTreeQCAvailable },
    } = useEnvironmentInfoContext();

    const {
        dataSources,
        fetchDataSources,
        status: datasourcesFetchStatus,
    } = useFetchDataSources();

    useEffect(() => {
        // Since AppNavBar lies outside the AppRouter (responsible for ensuring no
        // API calls are made without authentication), we need to add a manual check
        if (authenticated || authDisabled) {
            fetchDataSources();
        }
    }, [authenticated, authDisabled]);

    useEffect(() => {
        if (datasourcesFetchStatus === Status.DONE && !isEmpty(dataSources)) {
            // Get first available Pinot data source
            const pinotDataSource = getDefaultPinotDatasource(dataSources);

            // Determine query console URL
            setQueryConsoleUrl(
                getQueryConsoleUrl(
                    pinotDataSource ?? null,
                    accessToken,
                    isStarTreeQCAvailable
                )
            );
        }
    }, [datasourcesFetchStatus, isStarTreeQCAvailable]);

    const isRouteCurrent = (route: string): boolean => {
        return location.pathname.indexOf(route) === 0;
    };

    return (
        <NavBarV1
            maximizeLabel={t("label.maximize")}
            minimizeLabel={t("label.minimize")}
        >
            <NavBarPrimaryContainerV1>
                {(authenticated || authDisabled) && (
                    <>
                        {/* Overview */}
                        <NavBarLinkV1
                            data-testid="sidebar-overview-link"
                            href={AppRouteV2.OVERVIEW}
                            selected={isRouteCurrent(AppRouteV2.OVERVIEW)}
                        >
                            <NavBarLinkIconV1>
                                <HomeIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1>
                                {t("label.overview")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>

                        {/* Datasets */}
                        <NavBarLinkV1
                            data-testid="sidebar-datasets-link"
                            href={AppRouteV2.DATASETS}
                            selected={isRouteCurrent(AppRouteV2.DATASETS)}
                        >
                            <NavBarLinkIconV1>
                                <TableChartIcon />
                            </NavBarLinkIconV1>

                            <NavBarLinkTextV1>
                                {t("label.datasets")}
                            </NavBarLinkTextV1>
                        </NavBarLinkV1>

                        {/* Query Console */}
                        {queryConsoleUrl && (
                            <NavBarLinkV1
                                externalLink
                                href={queryConsoleUrl}
                                target="_blank"
                            >
                                <NavBarLinkIconV1>
                                    <CodeIcon />
                                </NavBarLinkIconV1>

                                <NavBarLinkTextV1>
                                    {t("label.query-console")}
                                </NavBarLinkTextV1>
                            </NavBarLinkV1>
                        )}
                    </>
                )}
            </NavBarPrimaryContainerV1>
        </NavBarV1>
    );
};
